import { Box, IconButton } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { CbhIcon, PromotionBanner } from "@src/appV2/redesign/system/ui-components";
import { useHistory } from "react-router-dom";

import { TAX_INFORMATION_FULL_PATH } from "../paths";
import { getTaxSeasonInfo, type TaxSeasonType } from "../utils/getTaxSeasonInfo";

export type TaxSeasonBannerSourceUI = "shifts-tab" | "account-tab";

interface TaxSeasonBannerProps {
  sourceUI: TaxSeasonBannerSourceUI;
  formIssuanceThresholdInDollars: number;
  taxationYear: number;
  timezone: string;
  onClose?: () => void;
}

function getTitle(taxSeason: TaxSeasonType) {
  if (taxSeason === "tax-preparation") {
    return "Get ready for Tax Season!";
  }

  return "It's Tax Season!";
}

function getSubtitle(
  taxSeason: TaxSeasonType,
  formIssuanceThresholdInDollars: TaxSeasonBannerProps["formIssuanceThresholdInDollars"],
  taxationYear: TaxSeasonBannerProps["taxationYear"]
) {
  if (taxSeason === "tax-preparation") {
    return "Make sure you're ready to receive your 1099-NEC form in January.";
  }

  return `Professionals who earned ${formatDollarsAsUsd(formIssuanceThresholdInDollars, {
    maximumFractionDigits: 0,
  })} or more through Clipboard in ${taxationYear} are issued a 1099-NEC form.`;
}

function getCtaButtonText(taxSeason: TaxSeasonType) {
  if (taxSeason === "tax-preparation") {
    return "Get Started";
  }

  return "Learn More";
}

export function TaxSeasonBanner(props: TaxSeasonBannerProps) {
  const { sourceUI, formIssuanceThresholdInDollars, taxationYear, timezone, onClose } = props;

  const history = useHistory();

  const { taxSeason } = getTaxSeasonInfo({ taxationYear, timezone });

  const title = getTitle(taxSeason);
  const subtitle = getSubtitle(taxSeason, formIssuanceThresholdInDollars, taxationYear);
  const ctaButtonText = getCtaButtonText(taxSeason);

  const onClick = () => {
    logEvent(APP_V2_USER_EVENTS.TAX_SEASON_BANNER_CTA_CLICKED, { taxSeason, sourceUI });
    history.push(TAX_INFORMATION_FULL_PATH);
  };

  const handleClose = (event: React.MouseEvent) => {
    event.stopPropagation();
    logEvent(APP_V2_USER_EVENTS.TAX_SEASON_BANNER_DISMISSED, { taxSeason, sourceUI });
    onClose?.();
  };

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      <PromotionBanner
        title={title}
        subtitle={subtitle}
        icon="document-green-colored"
        borderVariant="none"
        backgroundColor={(theme) => theme.palette.payment!.background}
        titleColor={(theme) => theme.palette.payment!.text}
        ctaButtonText={ctaButtonText}
        sx={{
          height: "100%",
          "& svg": {
            alignSelf: "flex-start",
          },
        }}
        onClick={onClick}
      />
      {onClose && (
        <IconButton
          aria-label="Close tax season banner"
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            padding: 1,
          }}
          onClick={handleClose}
        >
          <CbhIcon type="close" size="small" />
        </IconButton>
      )}
    </Box>
  );
}
